import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faUser,
	faPhone,
	faEnvelope,
	faSave,
} from "@fortawesome/free-solid-svg-icons";

import { Modal, Button, TextInput } from "../../../../components/common";
import {
	useCreateParentMutation,
	useUpdateParentMutation,
} from "../../../../store/api/parentApiSlice";

import styles from "./ParentModal.module.css";

const ParentModal = ({ parent, isOpen, onClose }) => {
	const [createParent, { isLoading: isCreating }] = useCreateParentMutation();
	const [updateParent, { isLoading: isUpdating }] = useUpdateParentMutation();

	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		email: "",
		phoneNumber: "",
		password: "",
	});

	const isEditing = !!parent;
	const isLoading = isCreating || isUpdating;

	useEffect(() => {
		if (parent) {
			setFormData({
				firstName: parent.firstName || "",
				lastName: parent.lastName || "",
				email: parent.email || "",
				phoneNumber: parent.phoneNumber || "",
				password: "", // Don't prefill password
			});
		} else {
			setFormData({
				firstName: "",
				lastName: "",
				email: "",
				phoneNumber: "",
				password: "",
			});
		}
	}, [parent]);

	const handleSubmit = async () => {
		try {
			if (isEditing) {
				await updateParent({ id: parent.id, ...formData }).unwrap();
			} else {
				await createParent(formData).unwrap();
			}
			onClose();
		} catch (error) {
			console.error("Error saving parent:", error);
			alert("Failed to save parent. Please try again.");
		}
	};

	const footer = (
		<>
			<Button variant="secondary" onClick={onClose} disabled={isLoading}>
				Cancel
			</Button>
			<Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
				<FontAwesomeIcon icon={faSave} />
				{isLoading ? "Saving..." : "Save Parent"}
			</Button>
		</>
	);

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={
				isEditing
					? `Edit ${parent.firstName} ${parent.lastName}`
					: "Add New Parent"
			}
			footer={footer}
			size="medium"
		>
			<div className={styles.parentForm}>
				<div className={styles.formSection}>
					<h3>
						<FontAwesomeIcon icon={faUser} />
						Personal Information
					</h3>
					<div className={styles.formGrid}>
						<TextInput
							label="First Name"
							value={formData.firstName}
							onChange={(e) =>
								setFormData({ ...formData, firstName: e.target.value })
							}
							required
						/>
						<TextInput
							label="Last Name"
							value={formData.lastName}
							onChange={(e) =>
								setFormData({ ...formData, lastName: e.target.value })
							}
							required
						/>
					</div>
				</div>

				<div className={styles.formSection}>
					<h3>
						<FontAwesomeIcon icon={faEnvelope} />
						Contact Information
					</h3>
					<div className={styles.formGrid}>
						<TextInput
							label="Email"
							type="email"
							value={formData.email}
							onChange={(e) =>
								setFormData({ ...formData, email: e.target.value })
							}
							required
						/>
						<TextInput
							label="Phone Number"
							value={formData.phoneNumber}
							onChange={(e) =>
								setFormData({ ...formData, phoneNumber: e.target.value })
							}
						/>
					</div>
				</div>

				<div className={styles.formSection}>
					<h3>Security</h3>
					<TextInput
						label="Password"
						type="password"
						value={formData.password}
						onChange={(e) =>
							setFormData({ ...formData, password: e.target.value })
						}
						placeholder={
							isEditing
								? "Leave blank to keep current password"
								: "Enter password"
						}
						required={!isEditing}
					/>
				</div>
			</div>
		</Modal>
	);
};

export default ParentModal;
